/**
 * ComplaintsListPage — `/customer-service/complaints`.
 *
 * Paginated list of service-desk cases with status + priority filters.
 * Wired to GET /v1/service-desk/cases on service-service-desk.
 */
import { type JSX, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { type CasePriority, type CaseStatus, listServiceCases } from '../../lib/api/service-desk';
import { PageHeader, StatusBadge } from '../shared/StaffUI';
import { FilterBar } from '../mail/shared/MailUI';

const STATUS_OPTIONS: readonly (CaseStatus | '')[] = [
  '',
  'OPEN',
  'IN_PROGRESS',
  'AWAITING_CUSTOMER',
  'ESCALATED',
  'RESOLVED',
  'CLOSED',
  'REJECTED',
];
const PRIORITY_OPTIONS: readonly (CasePriority | '')[] = ['', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

export function ComplaintsListPage(): JSX.Element {
  const [status, setStatus] = useState<CaseStatus | ''>('');
  const [priority, setPriority] = useState<CasePriority | ''>('');
  const [search, setSearch] = useState<string>('');

  const cases = useQuery({
    queryKey: ['cs', 'cases', 'list', { status, priority }],
    queryFn: () =>
      listServiceCases({
        ...(status !== '' && { status }),
        ...(priority !== '' && { priority }),
        pageSize: 25,
      }),
    retry: false,
    staleTime: 15_000,
  });

  const filtered = (cases.data?.items ?? []).filter((c) => {
    if (search.trim() === '') return true;
    const needle = search.trim().toLowerCase();
    return (
      c.title.toLowerCase().includes(needle) ||
      c.caseNumber.toLowerCase().includes(needle) ||
      c.category.toLowerCase().includes(needle) ||
      c.formKey.toLowerCase().includes(needle)
    );
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Complaints"
        description="Service-desk cases — operator queue."
        actions={
          <Link
            to="/customer-service/complaints/new"
            className="inline-flex items-center px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-semibold"
            data-testid="new-complaint-button"
          >
            File new complaint
          </Link>
        }
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search case # / title / category…"
      >
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as CaseStatus | '')}
          aria-label="Status filter"
          className="px-3 py-2 text-sm border border-border rounded-lg bg-white"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s || 'all'} value={s}>
              {s === '' ? 'All statuses' : s.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value as CasePriority | '')}
          aria-label="Priority filter"
          className="px-3 py-2 text-sm border border-border rounded-lg bg-white"
        >
          {PRIORITY_OPTIONS.map((p) => (
            <option key={p || 'all'} value={p}>
              {p === '' ? 'All priorities' : p}
            </option>
          ))}
        </select>
      </FilterBar>

      {cases.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : cases.isError ? (
        <div
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
          data-testid="complaints-error"
        >
          Failed to load cases: {(cases.error as Error).message}
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="complaints-table">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Case #</th>
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">Category</th>
                <th className="text-left px-4 py-2">Priority</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">SLA Target</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50" data-testid={`case-row-${c.id}`}>
                  <td className="px-4 py-2 font-mono text-xs text-gray-900">{c.caseNumber}</td>
                  <td className="px-4 py-2 text-xs max-w-48 truncate">{c.title}</td>
                  <td className="px-4 py-2 text-xs text-muted">{c.category}</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={c.priority} />
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {c.slaTarget ? new Date(c.slaTarget).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to={`/customer-service/complaints/${encodeURIComponent(c.id)}`}
                      className="text-xs font-semibold text-nipost-green hover:underline"
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted" data-testid="complaints-empty">
              No complaints match.
            </p>
          ) : null}
          {cases.data ? (
            <p className="px-4 py-2 text-xs text-muted border-t border-border">
              Showing {filtered.length} of {cases.data.total} case(s)
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
